// Active tool model + factory functions that produce a fresh Shape from a
// world-space rectangle (start + end pointer position). Canvas mouse handlers
// branch on `ToolId`; on mouseup they call the factory and addShape().
//
// Keep this file pure (no React, no Yjs) so it's trivially unit-testable.

import type {
  Arrow,
  Ellipse,
  Frame,
  Freehand,
  Image,
  Rect,
  Shape,
  Sticky,
  StickyColor,
  TextShape,
} from '@frame/shared';
import { newShapeId } from './binding/shapesMap';

export type ToolId =
  | 'select'
  | 'hand'
  | 'sticky'
  | 'sticky-wide'
  | 'frame'
  | 'rect'
  | 'ellipse'
  | 'text'
  | 'arrow'
  | 'pen'
  | 'image';

// Tools whose creation gesture is a drag (start → end world coords define
// the shape's bbox). Sticky and text are click-create with fixed defaults.
export type DragCreateTool = 'frame' | 'rect' | 'ellipse' | 'arrow' | 'pen';

// Default per-shape sizes for click-create. Stickies default to square 160×160;
// the actual size used is per-device remembered via stickyPrefs — these
// constants are the fallback when no remembered size exists yet.
// Text shape sizes itself to content.
export const DEFAULT_STICKY_W = 160;
export const DEFAULT_STICKY_H = 160;
// Wide sticky variant (Miro's "rectangle" sticky shape). Resize keeps this
// aspect ratio so a wide sticky stays wide unless the user drops a new
// variant from the sidebar.
export const WIDE_STICKY_W = 360;
export const WIDE_STICKY_H = 180;
export const MIN_DRAG_DISTANCE_SQ = 4 * 4; // pixels²; below this is a click

// Minimum dimensions for drag-created shapes so a stray click doesn't leave
// an invisible 1px rect behind.
export const MIN_SHAPE_SIZE = 16;

export interface WorldPoint {
  x: number;
  y: number;
}

interface NewShapeCtx {
  authorId: string;
  now?: number;
}

export function makeSticky(
  at: WorldPoint,
  ctx: NewShapeCtx,
  size?: { w: number; h: number },
  color?: StickyColor,
): Sticky {
  const w = size?.w ?? DEFAULT_STICKY_W;
  const h = size?.h ?? DEFAULT_STICKY_H;
  return {
    id: newShapeId('s_'),
    type: 'sticky',
    x: at.x - w / 2,
    y: at.y - h / 2,
    w,
    h,
    color: color ?? 'sand',
    text: '',
    authorId: ctx.authorId,
    createdAt: ctx.now ?? Date.now(),
  };
}

export function makeText(at: WorldPoint, ctx: NewShapeCtx): TextShape {
  return {
    id: newShapeId('t_'),
    type: 'text',
    x: at.x,
    y: at.y,
    text: '',
    fontSize: 16,
    authorId: ctx.authorId,
    createdAt: ctx.now ?? Date.now(),
  };
}

export function makeFrame(start: WorldPoint, end: WorldPoint, ctx: NewShapeCtx): Frame {
  const box = normalizeBox(start, end);
  return {
    id: newShapeId('f_'),
    type: 'frame',
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
    label: 'Frame',
    authorId: ctx.authorId,
    createdAt: ctx.now ?? Date.now(),
  };
}

export function makeRect(start: WorldPoint, end: WorldPoint, ctx: NewShapeCtx): Rect {
  const box = normalizeBox(start, end);
  return {
    id: newShapeId('r_'),
    type: 'rect',
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
    fill: '#ffffff',
    stroke: '#1a1a1a',
    authorId: ctx.authorId,
    createdAt: ctx.now ?? Date.now(),
  };
}

export function makeEllipse(start: WorldPoint, end: WorldPoint, ctx: NewShapeCtx): Ellipse {
  const box = normalizeBox(start, end);
  return {
    id: newShapeId('e_'),
    type: 'ellipse',
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
    fill: '#ffffff',
    stroke: '#1a1a1a',
    authorId: ctx.authorId,
    createdAt: ctx.now ?? Date.now(),
  };
}

// Arrows in v1 use `kind: 'point'` endpoints — no shape-anchored binding.
// Auto-binding is risk R2 in the spec; revisit post-launch.
export function makeArrow(start: WorldPoint, end: WorldPoint, ctx: NewShapeCtx): Arrow {
  return {
    id: newShapeId('a_'),
    type: 'arrow',
    x: 0,
    y: 0,
    from: { kind: 'point', x: start.x, y: start.y },
    to: { kind: 'point', x: end.x, y: end.y },
    authorId: ctx.authorId,
    createdAt: ctx.now ?? Date.now(),
  };
}

// Constrain a candidate image size to a sensible default (max 600px on the
// long edge) while preserving aspect ratio. Keeps a 3000×2000 phone photo
// from blowing past the screen on drop. Users can resize manually once that
// lands.
export const MAX_IMAGE_DIMENSION = 600;

export function fitImageSize(naturalW: number, naturalH: number): { w: number; h: number } {
  if (naturalW <= 0 || naturalH <= 0) {
    return { w: MAX_IMAGE_DIMENSION, h: MAX_IMAGE_DIMENSION };
  }
  const long = Math.max(naturalW, naturalH);
  if (long <= MAX_IMAGE_DIMENSION) return { w: naturalW, h: naturalH };
  const scale = MAX_IMAGE_DIMENSION / long;
  return { w: Math.round(naturalW * scale), h: Math.round(naturalH * scale) };
}

export function makeImage(
  at: WorldPoint,
  src: string,
  size: { w: number; h: number },
  ctx: NewShapeCtx,
  alt?: string,
): Image {
  return {
    id: newShapeId('i_'),
    type: 'image',
    x: at.x - size.w / 2,
    y: at.y - size.h / 2,
    w: size.w,
    h: size.h,
    src,
    alt,
    authorId: ctx.authorId,
    createdAt: ctx.now ?? Date.now(),
  };
}

// Freehand stores raw alternating [x0,y0,x1,y1,...] for direct Konva Line use.
// `points` is in world-space and not translated by shape.x/y (we render Line
// at origin (0,0) and let the polyline carry its own coords). This matches
// what ShapeRenderer reads today.
export function makeFreehand(points: number[], ctx: NewShapeCtx): Freehand {
  return {
    id: newShapeId('p_'),
    type: 'freehand',
    x: 0,
    y: 0,
    points,
    stroke: '#1a1a1a',
    strokeWidth: 2,
    authorId: ctx.authorId,
    createdAt: ctx.now ?? Date.now(),
  };
}

// Normalize two diagonal corners into an axis-aligned bbox with positive
// width / height. Enforces MIN_SHAPE_SIZE so a tiny accidental drag still
// yields a usable shape — the user can resize once that lands.
export function normalizeBox(
  a: WorldPoint,
  b: WorldPoint,
): { x: number; y: number; w: number; h: number } {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.max(MIN_SHAPE_SIZE, Math.abs(b.x - a.x));
  const h = Math.max(MIN_SHAPE_SIZE, Math.abs(b.y - a.y));
  return { x, y, w, h };
}

// Which shapes can be text-edited via the DOM overlay. Arrows/freehand have
// no text field today; frame labels are editable.
export function isTextEditable(shape: Shape): boolean {
  return (
    shape.type === 'sticky' ||
    shape.type === 'rect' ||
    shape.type === 'ellipse' ||
    shape.type === 'text' ||
    shape.type === 'frame'
  );
}
